import {
  Outlet,
  createFileRoute,
  linkOptions,
  useMatch,
} from '@tanstack/react-router'
import { useAtomValue } from 'jotai'
import { useTranslation } from 'react-i18next'
import {
  type DateFilter,
  InsuranceCompany,
  type PolicyFilters,
  type PolicyResponse,
  type PolicySortingState,
  PolicyStatus,
  policySortingColumns,
} from 'types'
import z from 'zod'

import { queryPolicyCount, queryPolicyList } from '@api/policy/policy.queries'

import StreamingExportDialog from '@/components/export/streaming-export-dialog'
import { ExportType } from '@/components/export/streaming-export-dialog.types'
import PolicyStatusTabs from '@/components/filters/policy-status-tabs'
import InsuranceCompanySelect from '@/components/insurance-company-select'
import LastImportSubtitle from '@/components/last-import-subtitle'
import PortfolioSelect from '@/components/portfolio-select'
import SearchBar from '@/components/search-bar'
import type { CursorPaginationState } from '@/components/table/cursor-pagination'
import DateFilterDialog from '@/components/table/date-filter-dialog'
import PoliciesTable from '@/components/tables/policies/policies-table'
import { dateFilterSchema, numberFilterSchema } from '@/lib/filter'
import { cursorPaginationSchema, sortingSchema } from '@/lib/table'
import { insuranceCompanyFilterAtom } from '@/stores/insurance-company-filter.atom'
import { pageSearchAtom, storePageSearch } from '@/stores/page-search.atom'
import { portfolioFilterAtom } from '@/stores/portfolio-filter.atom'
import { queryImportRunsLatest } from '@api/import-run/import-run.queries'
import { useQuery } from '@tanstack/react-query'

import SectionHeader from '../../-components/section-layout/section-header'
import PoliciesSortSelect from './-components/policies-sort-select'

const searchSchema = z.object({
  pagination: cursorPaginationSchema,
  sorting: sortingSchema(policySortingColumns),
  filters: z
    .object({
      search: z.string().optional(),
      insuranceCompany: z.enum(InsuranceCompany).optional(),
      status: z.enum(PolicyStatus).optional(),
      companyName: z.string().optional(),
      groupName: z.string().optional(),
      policyNumber: z.string().optional(),
      currencyCode: z.string().optional(),
      expirationDate: dateFilterSchema.optional(),
      lastActivityDate: dateFilterSchema.optional(),
      activeCreditLimits: numberFilterSchema.optional(),
      insistences: numberFilterSchema.optional(),
      type: z.string().optional(),
      acceptanceRate: numberFilterSchema.optional(),
    })
    .optional(),
})

type Search = z.infer<typeof searchSchema>

export const Route = createFileRoute('/app/data/policies')({
  component: RouteComponent,
  validateSearch: searchSchema,
  loaderDeps: ({ search }) => search,
  loader: ({ context: { queryClient }, deps: { pagination, sorting } }) => {
    queryClient.prefetchQuery(
      queryPolicyList({
        ...pagination,
        sorting,
      }),
    )
    queryClient.prefetchQuery(queryPolicyCount())
  },
  beforeLoad: ({ search, cause, location }) => {
    storePageSearch(
      Route.fullPath,
      { to: Route.fullPath, search },
      location,
      cause,
    )
  },
})

const rowLink = (row: PolicyResponse) =>
  linkOptions({
    to: '/app/data/policies/$uuid',
    params: { uuid: row.uuid },
    viewTransition: false,
  })

function RouteComponent() {
  const { t } = useTranslation()
  const routeSearch = Route.useSearch()
  const navigate = Route.useNavigate()
  const portfolioFilter = useAtomValue(portfolioFilterAtom)
  const insuranceCompanyFilter = useAtomValue(insuranceCompanyFilterAtom)
  const { data: latestImportRuns } = useQuery(queryImportRunsLatest())

  const filters: PolicyFilters = {
    ...routeSearch.filters,
    portfolioUserUuid: portfolioFilter,
    insuranceCompany: insuranceCompanyFilter,
  }

  // If user is on a policy detail sheet,
  // don't change the search params to keep
  // the filters and sorting applied on the table.
  const pageSearch = useAtomValue(pageSearchAtom)
  const match = useMatch({
    shouldThrow: false,
    from: '/app/data/policies/$uuid',
  })
  const tableFrozen = !!match
  const search = tableFrozen
    ? // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      ((pageSearch[Route.fullPath]?.search as undefined | Search) ??
      routeSearch)
    : routeSearch

  const setFilters = (updater: (filter?: PolicyFilters) => PolicyFilters) =>
    navigate({
      search: (previous) => ({
        ...previous,
        filters: updater(previous.filters),
        // reset pagination to the first page on filter change
        pagination: { ...previous.pagination, cursor: undefined },
      }),
    })

  const setPagination = (
    updater: (pagination: CursorPaginationState) => CursorPaginationState,
  ) =>
    navigate({
      search: (previous) => ({
        ...previous,
        pagination: updater(previous.pagination),
      }),
    })

  const setSorting = (
    updater: (sorting?: PolicySortingState) => PolicySortingState | undefined,
  ) =>
    navigate({
      search: (previous) => ({
        ...previous,
        sorting: updater(previous.sorting),
      }),
    })

  const handleStatusChange = (status: PolicyStatus | undefined) =>
    setFilters((previous) => ({ ...previous, status }))

  const setSearchText = (search: string) =>
    setFilters((previous) => ({ ...previous, search }))

  const setExpirationDateFilter = (expirationDate: DateFilter | undefined) =>
    setFilters((previous) => ({ ...previous, expirationDate }))

  return (
    <div className="flex h-full grow flex-col divide-y overflow-hidden">
      <SectionHeader
        isSecondary
        title={t('data.policies.title')}
        subtitle={
          <LastImportSubtitle
            data={latestImportRuns}
            activeInsurer={insuranceCompanyFilter}
          />
        }
      >
        <div className="flex items-center gap-2">
          <SearchBar
            tabIndex={1}
            className="max-w-62.75"
            text={filters.search}
            setText={setSearchText}
          />
          <InsuranceCompanySelect />
          <div className="w-47.75">
            <DateFilterDialog
              placeholder={t('table.columnFilters.date.placeholder')}
              onChange={setExpirationDateFilter}
              value={filters.expirationDate}
              triggerButtonProps={dateFilterTriggerButtonProps}
            />
          </div>
          <PoliciesSortSelect
            sorting={search.sorting}
            setSorting={setSorting}
          />
          <PortfolioSelect />
          <StreamingExportDialog type={ExportType.Policies} filters={filters} />
        </div>
      </SectionHeader>
      <div className="flex h-full flex-col gap-4 overflow-hidden p-2">
        <PolicyStatusTabs
          value={filters.status}
          onValueChange={handleStatusChange}
          filters={filters}
        />
        <PoliciesTable
          filters={filters}
          setFilters={setFilters}
          pagination={search.pagination}
          setPagination={setPagination}
          sorting={search.sorting}
          setSorting={setSorting}
          rowLink={rowLink}
        />
      </div>
      <Outlet />
    </div>
  )
}

const dateFilterTriggerButtonProps = {
  size: 'sm',
} as const
